import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Form, Button, Row, Col, Spinner } from 'react-bootstrap';
import { Stars, Trash, PencilSquare, Check2, X, PlusLg } from 'react-bootstrap-icons';
import { AdminCard } from '../shared/AdminCard';

const API = `${process.env.REACT_APP_CORE_API}/chatbot`;

function FaqEditor({ value, onChange }) {
  return (
    <>
      <Form.Group className="mb-2">
        <Form.Label className="small fw-semibold mb-1">Question</Form.Label>
        <Form.Control
          value={value.question}
          onChange={(e) => onChange({ ...value, question: e.target.value })}
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label className="small fw-semibold mb-1">
          Keywords <span className="text-muted fw-normal">(comma separated — the bot matches on these)</span>
        </Form.Label>
        <Form.Control
          value={value.keywordsText}
          onChange={(e) => onChange({ ...value, keywordsText: e.target.value })}
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label className="small fw-semibold mb-1">Answer</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={value.answer}
          onChange={(e) => onChange({ ...value, answer: e.target.value })}
        />
      </Form.Group>
    </>
  );
}

const toEditable = (f) => ({ ...f, keywordsText: (f.keywords || []).join(', ') });
const toPayload = (f) => ({
  question: f.question,
  answer: f.answer,
  keywords: f.keywordsText.split(',').map(k => k.trim()).filter(Boolean),
});

export function ChatbotAdmin({ idToken }) {
  const headers = { Authorization: idToken };

  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);

  const [topic, setTopic] = useState('');
  const [notes, setNotes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [error, setError] = useState(null);

  const fetchFaqs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/faq`);
      setFaqs(res.data || []);
    } catch (err) {
      console.error('Failed to load FAQs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFaqs(); }, [fetchFaqs]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setError(null);
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/generate`, { topic, notes }, { headers });
      setDrafts((res.data || []).map(toEditable));
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const saveDraft = async (idx) => {
    const d = drafts[idx];
    try {
      const res = await axios.post(`${API}/faq`, toPayload(d), { headers });
      setFaqs(prev => [...prev, res.data]);
      setDrafts(prev => prev.filter((_, i) => i !== idx));
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data || 'Save failed');
    }
  };

  const saveAllDrafts = async () => {
    for (let i = drafts.length - 1; i >= 0; i--) {
      // eslint-disable-next-line no-await-in-loop
      await saveDraft(i);
    }
  };

  const startEdit = (f) => { setEditingId(f.id); setEditDraft(toEditable(f)); };
  const cancelEdit = () => { setEditingId(null); setEditDraft(null); };

  const saveEdit = async () => {
    try {
      const res = await axios.put(`${API}/faq/${editingId}`, toPayload(editDraft), { headers });
      setFaqs(prev => prev.map(f => (f.id === editingId ? res.data : f)));
      cancelEdit();
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data || 'Save failed');
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/faq/${id}`, { headers });
      setFaqs(prev => prev.filter(f => f.id !== id));
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data || 'Delete failed');
    }
  };

  return (
    <div>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Chatbot</h1>
        <p className="admin-page-description">
          Manage what the homepage chatbot can answer. Generate drafts with AI, edit them, then add the ones you want.
        </p>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">{error}</div>
      )}

      <AdminCard>
        <Form onSubmit={handleGenerate}>
          <Row className="g-3 align-items-end">
            <Col md={5}>
              <Form.Label className="small fw-semibold mb-1">Topic</Form.Label>
              <Form.Control
                placeholder="e.g. injuries, how payouts work, attorney sign up"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                required
              />
            </Col>
            <Col md={7}>
              <Button type="submit" disabled={generating || !topic.trim()} className="w-100">
                {generating ? <><Spinner size="sm" className="me-2" />Generating...</> : <><Stars className="me-2" />Generate with AI</>}
              </Button>
            </Col>
            <Col md={12}>
              <Form.Label className="small fw-semibold mb-1">
                Facts to include <span className="text-muted fw-normal">(optional)</span>
              </Form.Label>
              <Form.Control
                as="textarea" rows={2}
                placeholder="Anything specific the answers should say — coverage areas, response times, pricing rules..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Col>
          </Row>
        </Form>
      </AdminCard>

      {drafts.length > 0 && (
        <AdminCard className="mt-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Drafts <span className="text-muted fw-normal">({drafts.length})</span></h5>
            <div className="d-flex gap-2">
              <Button size="sm" variant="outline-secondary" onClick={() => setDrafts([])}>Discard all</Button>
              <Button size="sm" onClick={saveAllDrafts}><PlusLg className="me-1" />Add all</Button>
            </div>
          </div>
          {drafts.map((d, idx) => (
            <div key={idx} className="p-3 border rounded mb-3 bg-light">
              <FaqEditor value={d} onChange={(v) => setDrafts(prev => prev.map((x, i) => (i === idx ? v : x)))} />
              <div className="d-flex gap-2 justify-content-end">
                <Button size="sm" variant="outline-secondary" onClick={() => setDrafts(prev => prev.filter((_, i) => i !== idx))}>
                  <X className="me-1" />Discard
                </Button>
                <Button size="sm" onClick={() => saveDraft(idx)}>
                  <PlusLg className="me-1" />Add
                </Button>
              </div>
            </div>
          ))}
        </AdminCard>
      )}

      <AdminCard className="mt-4">
        <h5 className="mb-3">Live entries <span className="text-muted fw-normal">({faqs.length})</span></h5>
        {loading ? (
          <div className="admin-loading"><div className="admin-loading-spinner" /></div>
        ) : faqs.length === 0 ? (
          <p className="text-muted mb-0">No entries yet. Generate some above.</p>
        ) : (
          faqs.map(f => (
            <div key={f.id} className="py-3 border-bottom">
              {editingId === f.id ? (
                <>
                  <FaqEditor value={editDraft} onChange={setEditDraft} />
                  <div className="d-flex gap-2 justify-content-end">
                    <Button size="sm" variant="outline-secondary" onClick={cancelEdit}><X className="me-1" />Cancel</Button>
                    <Button size="sm" onClick={saveEdit}><Check2 className="me-1" />Save</Button>
                  </div>
                </>
              ) : (
                <div className="d-flex justify-content-between gap-3">
                  <div>
                    <div className="fw-semibold">{f.question}</div>
                    <div className="text-muted small mb-1">{f.answer}</div>
                    <div>
                      {f.keywords.map(k => (
                        <span key={k} className="badge bg-light text-dark border me-1">{k}</span>
                      ))}
                    </div>
                  </div>
                  <div className="d-flex gap-1 align-items-start flex-shrink-0">
                    <Button size="sm" variant="link" className="text-secondary p-1" onClick={() => startEdit(f)} title="Edit">
                      <PencilSquare size={16} />
                    </Button>
                    <Button size="sm" variant="link" className="text-danger p-1" onClick={() => remove(f.id)} title="Delete">
                      <Trash size={16} />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </AdminCard>
    </div>
  );
}
